import { EntryCollection } from "@/types/entryCollection";
import {
  AnalysisReviewStatus,
  AnalysisStatus,
  ImageMeasurementEntry,
  LeafAreaIndexEntry,
  StandCountEntry,
} from "@/types/entry";
import { EntryConfigUnion, IMAGE_MEASUREMENT_MODEL_KINDS } from "@/types/entryConfig";
import { timestampToMillis } from "@shared/deferredAnalysis";

export const STALE_DEFERRED_ANALYSIS_MS = 45 * 60 * 1000;

const DEFERRED_IMAGE_MEASUREMENT_MODEL_KINDS = new Set<string>([
  IMAGE_MEASUREMENT_MODEL_KINDS.OPEN_SET_OBJECT_DETECTOR,
  IMAGE_MEASUREMENT_MODEL_KINDS.STRAWBERRY,
  IMAGE_MEASUREMENT_MODEL_KINDS.APPLE,
  IMAGE_MEASUREMENT_MODEL_KINDS.TOMATO,
  IMAGE_MEASUREMENT_MODEL_KINDS.BLUEBERRY,
  IMAGE_MEASUREMENT_MODEL_KINDS.SQUASH,
  IMAGE_MEASUREMENT_MODEL_KINDS.CUCUMBER,
  IMAGE_MEASUREMENT_MODEL_KINDS.BISECTED_LETTUCE,
]);

export interface DeferredAnalysisSummary {
  supported: boolean;
  pendingCount: number;
  processingCount: number;
  staleProcessingCount: number;
  failedCount: number;
  completedCount: number;
  unreviewedCount: number;
  eligibleCount: number;
  disabledReason?: string;
}

export interface DeferredAnalysisPlotStatus {
  status?: Exclude<AnalysisStatus, "completed"> | "stale";
  error?: string;
  unreviewed?: boolean;
}

export interface DeferredAnalysisReviewFields {
  analysisStatus?: AnalysisStatus;
  analysisReviewStatus?: AnalysisReviewStatus;
}

export interface AnnotatedImageRenderFields {
  captureMetadataCloudPath?: string;
  analysisStatus?: AnalysisStatus;
  dataCollectionOnly?: boolean;
}

type DeferredAnalysisEntry = ImageMeasurementEntry | StandCountEntry | LeafAreaIndexEntry;
export type DeferredAnalysisEntryMapField =
  | "imageMeasurementEntries"
  | "standCountMeasurementEntries"
  | "leafAreaIndexEntries";

export function getDeferredAnalysisEntryMapFieldForImageType(
  imageType: string | undefined
): DeferredAnalysisEntryMapField | null {
  if (imageType === "imageMeasurement") return "imageMeasurementEntries";
  if (imageType === "standCountMeasurement") return "standCountMeasurementEntries";
  if (imageType === "leafAreaIndex") return "leafAreaIndexEntries";
  return null;
}

function getCollectionEntryConfigId(collection: EntryCollection): string | undefined {
  return typeof collection.entryConfigRef === "string"
    ? collection.entryConfigRef
    : collection.entryConfigRef?.id;
}

function getImageMeasurementEntries(
  collections: EntryCollection[],
  entryConfigId: string
): ImageMeasurementEntry[] {
  return collections
    .filter((collection) => (
      collection.type === "imageMeasurement" &&
      getCollectionEntryConfigId(collection) === entryConfigId
    ))
    .flatMap((collection) => Object.values(collection.imageMeasurementEntries ?? {}));
}

function getStandCountEntries(
  collections: EntryCollection[],
  entryConfigId: string
): StandCountEntry[] {
  return collections
    .filter((collection) => (
      collection.type === "standCountMeasurement" &&
      getCollectionEntryConfigId(collection) === entryConfigId
    ))
    .flatMap((collection) => Object.values(collection.standCountMeasurementEntries ?? {}));
}

function getLeafAreaIndexEntries(
  collections: EntryCollection[],
  entryConfigId: string
): LeafAreaIndexEntry[] {
  return collections
    .filter((collection) => (
      collection.type === "leafAreaIndex" &&
      getCollectionEntryConfigId(collection) === entryConfigId
    ))
    .flatMap((collection) => Object.values(collection.leafAreaIndexEntries ?? {}));
}

function getDeferredAnalysisEntriesForConfig(
  collections: EntryCollection[],
  entryConfigId: string,
  config: EntryConfigUnion | undefined
): DeferredAnalysisEntry[] {
  if (config?.type === "imageMeasurement") {
    return getImageMeasurementEntries(collections, entryConfigId);
  }
  if (config?.type === "standCountMeasurement") {
    return getStandCountEntries(collections, entryConfigId);
  }
  if (config?.type === "leafAreaIndex") {
    return getLeafAreaIndexEntries(collections, entryConfigId);
  }
  return [];
}

export function isStaleDeferredAnalysisEntry(
  entry: Pick<DeferredAnalysisEntry, "analysisStatus" | "analysisStartedAt">,
  nowMs = Date.now()
): boolean {
  if (entry.analysisStatus !== "processing") return false;
  const startedAtMs = timestampToMillis(entry.analysisStartedAt);
  return startedAtMs !== undefined && nowMs - startedAtMs >= STALE_DEFERRED_ANALYSIS_MS;
}

export function isDeferredAnalysisSupportedConfig(config: EntryConfigUnion | undefined): boolean {
  if (config?.type === "imageMeasurement") {
    const kind = config.imageMeasurementConfig?.imageMeasurementModelOptions?.kind;
    return Boolean(kind && DEFERRED_IMAGE_MEASUREMENT_MODEL_KINDS.has(kind));
  }

  if (config?.type === "standCountMeasurement") {
    const kind = config.standCountMeasurementConfig?.imgMeasureConfig?.imageMeasurementModelOptions?.kind;
    return Boolean(kind);
  }

  if (config?.type === "leafAreaIndex") {
    return Boolean(config.leafAreaIndexConfig);
  }

  return false;
}

export function isDeferredAnalysisReviewableEntry(entry: DeferredAnalysisReviewFields | null | undefined): boolean {
  return (
    entry?.analysisStatus === "completed" &&
    (entry.analysisReviewStatus === "deferred_unreviewed" || entry.analysisReviewStatus === "reviewed")
  );
}

export function isDeferredAnalysisUnreviewedEntry(entry: DeferredAnalysisReviewFields | null | undefined): boolean {
  return entry?.analysisStatus === "completed" && entry.analysisReviewStatus === "deferred_unreviewed";
}

export function isDeferredAnalysisReviewedEntry(entry: DeferredAnalysisReviewFields | null | undefined): boolean {
  return entry?.analysisStatus === "completed" && entry.analysisReviewStatus === "reviewed";
}

export function shouldRenderAnnotatedImage(
  image: AnnotatedImageRenderFields | null | undefined
): image is AnnotatedImageRenderFields & { captureMetadataCloudPath: string } {
  if (!image?.captureMetadataCloudPath || image.dataCollectionOnly === true) return false;
  return true;
}

export function canEditRenderedAnnotationImage(
  image: AnnotatedImageRenderFields | null | undefined
): image is AnnotatedImageRenderFields & { captureMetadataCloudPath: string } {
  if (!shouldRenderAnnotatedImage(image)) return false;
  return image.analysisStatus === undefined || image.analysisStatus === "completed";
}

export function summarizeDeferredAnalysisForCollections(
  collections: EntryCollection[],
  entryConfigId: string | null,
  config: EntryConfigUnion | undefined
): DeferredAnalysisSummary {
  const supported = isDeferredAnalysisSupportedConfig(config);

  if (!entryConfigId || !supported) {
    return {
      supported,
      pendingCount: 0,
      processingCount: 0,
      staleProcessingCount: 0,
      failedCount: 0,
      completedCount: 0,
      unreviewedCount: 0,
      eligibleCount: 0,
      disabledReason: supported ? "Select a trait before running analysis." : "Pending analysis is not available for this trait.",
    };
  }

  const entries = getDeferredAnalysisEntriesForConfig(collections, entryConfigId, config);
  const uploadedEntries = entries.filter((entry) => entry.uploadStatus === "uploaded");
  const pendingCount = uploadedEntries.filter((entry) => entry.analysisStatus === "pending").length;
  const staleProcessingCount = uploadedEntries.filter((entry) => isStaleDeferredAnalysisEntry(entry)).length;
  const processingCount = uploadedEntries.filter(
    (entry) => entry.analysisStatus === "processing" && !isStaleDeferredAnalysisEntry(entry)
  ).length;
  const failedCount = uploadedEntries.filter((entry) => entry.analysisStatus === "failed").length;
  const completedCount = uploadedEntries.filter((entry) => entry.analysisStatus === "completed").length;
  const unreviewedCount = uploadedEntries.filter(isDeferredAnalysisUnreviewedEntry).length;
  const eligibleCount = pendingCount + failedCount + staleProcessingCount;

  return {
    supported,
    pendingCount,
    processingCount,
    staleProcessingCount,
    failedCount,
    completedCount,
    unreviewedCount,
    eligibleCount,
    disabledReason: eligibleCount === 0
      ? "No uploaded pending or failed captures are available for this trait."
      : undefined,
  };
}

export function summarizeDeferredAnalysisForTask(
  collections: EntryCollection[],
  configs: EntryConfigUnion[]
): DeferredAnalysisSummary {
  const supportedConfigs = configs.filter((config) => config.id && isDeferredAnalysisSupportedConfig(config));

  if (supportedConfigs.length === 0) {
    return {
      supported: false,
      pendingCount: 0,
      processingCount: 0,
      staleProcessingCount: 0,
      failedCount: 0,
      completedCount: 0,
      unreviewedCount: 0,
      eligibleCount: 0,
      disabledReason: "Pending analysis is not available for this task.",
    };
  }

  const summary = supportedConfigs.reduce<DeferredAnalysisSummary>(
    (combined, config) => {
      const summary = summarizeDeferredAnalysisForCollections(collections, config.id ?? null, config);
      return {
        supported: true,
        pendingCount: combined.pendingCount + summary.pendingCount,
        processingCount: combined.processingCount + summary.processingCount,
        staleProcessingCount: combined.staleProcessingCount + summary.staleProcessingCount,
        failedCount: combined.failedCount + summary.failedCount,
        completedCount: combined.completedCount + summary.completedCount,
        unreviewedCount: combined.unreviewedCount + summary.unreviewedCount,
        eligibleCount: combined.eligibleCount + summary.eligibleCount,
        disabledReason: undefined,
      };
    },
    {
      supported: true,
      pendingCount: 0,
      processingCount: 0,
      staleProcessingCount: 0,
      failedCount: 0,
      completedCount: 0,
      unreviewedCount: 0,
      eligibleCount: 0,
    }
  );

  return {
    ...summary,
    disabledReason: summary.eligibleCount === 0
      ? "No uploaded pending or failed captures are available for this task."
      : undefined,
  };
}

export function getDeferredAnalysisPlotStatus(
  collections: EntryCollection[],
  entryConfigId: string | null,
  config?: EntryConfigUnion
): DeferredAnalysisPlotStatus | null {
  if (!entryConfigId) return null;

  const entries = config
    ? getDeferredAnalysisEntriesForConfig(collections, entryConfigId, config)
    : getImageMeasurementEntries(collections, entryConfigId);
  const hasUnreviewed = entries.some(
    (entry) => entry.uploadStatus === "uploaded" && isDeferredAnalysisUnreviewedEntry(entry)
  );

  const failedEntry = entries.find((entry) => entry.uploadStatus === "uploaded" && entry.analysisStatus === "failed");
  if (failedEntry) {
    return { status: "failed", error: failedEntry.analysisError, unreviewed: hasUnreviewed || undefined };
  }

  if (entries.some((entry) => entry.uploadStatus === "uploaded" && isStaleDeferredAnalysisEntry(entry))) {
    return { status: "stale", unreviewed: hasUnreviewed || undefined };
  }

  if (
    entries.some(
      (entry) => entry.uploadStatus === "uploaded" &&
        entry.analysisStatus === "processing" &&
        !isStaleDeferredAnalysisEntry(entry)
    )
  ) {
    return { status: "processing", unreviewed: hasUnreviewed || undefined };
  }

  if (entries.some((entry) => entry.uploadStatus === "uploaded" && entry.analysisStatus === "pending")) {
    return { status: "pending", unreviewed: hasUnreviewed || undefined };
  }

  return hasUnreviewed ? { unreviewed: true } : null;
}
